import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Form, Formik } from 'formik'
import { Modal, Typography } from '@mui/material'
import { CalendarMinor } from '@shopify/polaris-icons'
import tw from 'twin.macro'
import dayjs from 'dayjs'
import {
  getExpirationMaxDate,
  getExpirationMinDate,
  MAX_DATE_ERROR,
  MAX_EXPIRATION_DATE_HINT,
} from 'src/utils/expirationDateUtils'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { Button } from 'src/components/shared/Button'
import { FormikDatePicker } from 'src/components/shared/form/formik/FormikDatePicker'
import { FormikRadioGroup } from 'src/components/shared/form/formik/FormikRadioGroup'
import { ApproveOnboardingStep } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { useDispatch } from 'react-redux'
import { CollectedData } from 'src/interfaces/collectedData'
import { ContractorDetails } from 'src/interfaces/contractor'
import { ApproveStepAction, ApproveStepFormValues } from './ApproveStepForm.interface'
import { getApproveStepFormSchema } from './ApproveStepForm.validator'
import { colors } from 'src/theme'

interface Props {
  isOpen: boolean
  collectedData: CollectedData
  contractorDetails: ContractorDetails
  isCombinedStep: boolean
  isExpirationRequired: boolean
  onClose(): void
  onSuccess(): void | Promise<void>
}

const ACTION_OPTIONS = [
  { label: 'Make the new submission effective immediately', value: ApproveStepAction.Immediately },
  { label: 'Choose when the new submission becomes effective', value: ApproveStepAction.Choose },
]

const ApproveStepModalComponent = ({
  isOpen,
  collectedData,
  contractorDetails,
  isCombinedStep,
  isExpirationRequired,
  onClose,
  onSuccess,
}: Props) => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)

  const isMountedRef = useRef(true)
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const container = useMemo(() => document.getElementById('root'), [])

  const validationSchema = useMemo(
    () => getApproveStepFormSchema(isExpirationRequired),
    [isExpirationRequired],
  )

  const initialValues: ApproveStepFormValues = useMemo(
    () => ({
      action: ApproveStepAction.Immediately,
      effectiveAt: null,
      expiresAt: collectedData.expiresAt ?? null,
    }),
    [collectedData.expiresAt],
  )

  const handleSubmit = useCallback(
    async (values: ApproveStepFormValues) => {
      setIsLoading(true)

      try {
        const requestPayload: {
          stepId: string
          partnerDriverId: string
          effectiveAt?: string
          expiresAt?: string
        } = {
          stepId: collectedData.collectDataStep.id,
          partnerDriverId: contractorDetails.id,
        }

        if (values.action === ApproveStepAction.Choose && values.effectiveAt) {
          requestPayload.effectiveAt = values.effectiveAt
        }

        if (values.expiresAt) {
          requestPayload.expiresAt = values.expiresAt
        }

        await createAsyncAction(dispatch, ApproveOnboardingStep.request(requestPayload))

        await onSuccess()

        if (isMountedRef.current) {
          onClose()
          showToast('Step is approved.')
        }
      } catch (err) {
        handleApiError(err)
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false)
        }
      }
    },
    [collectedData, contractorDetails, dispatch, onSuccess, onClose],
  )

  return (
    <Modal
      container={container}
      open={isOpen}
      onClose={onClose}
      aria-labelledby="approve-step-modal-title"
      css={tw`flex justify-center items-center backdrop-blur`}
    >
      <div
        data-testid="contractor-details-approve-step-modal"
        css={tw`bg-white rounded-lg w-[420px]`}
      >
        <ModalHeader
          title="Approve submission"
          onClose={onClose}
          closeButtonDataTestId="contractor-details-approve-step-modal-close-btn"
        />
        <div css={tw`px-4 pb-3`}>
          <div css={tw`my-4`}>
            {isCombinedStep && (
              <Fragment>
                <Typography variant="subtitle1" css={tw`font-semibold text-black`}>
                  There&apos;s already an active submission for this step
                </Typography>
                <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mt-0.5`}>
                  Choose when the new submission should take effect.
                </Typography>
              </Fragment>
            )}
            {isExpirationRequired ? (
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                {MAX_EXPIRATION_DATE_HINT}
              </Typography>
            ) : (
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                You can also set an expiration date (optional).
              </Typography>
            )}
          </div>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ handleSubmit: formikHandleSubmit, values, setFieldValue }) => {
              const isChooseAction = values.action === ApproveStepAction.Choose
              const expirationMinDateOptions = getExpirationMinDate(values.effectiveAt)

              const handleActionChange = (e: React.ChangeEvent<any>) => {
                const newAction = e.target.value
                setFieldValue('action', newAction)

                if (newAction === ApproveStepAction.Immediately) {
                  setFieldValue('effectiveAt', null)
                }
              }

              return (
                <Form css={tw`flex flex-col`}>
                  <div css={tw`flex flex-col gap-4`}>
                    {isCombinedStep && (
                      <FormikRadioGroup<ApproveStepFormValues>
                        name="action"
                        label="Select action"
                        options={ACTION_OPTIONS}
                        onChange={handleActionChange}
                      />
                    )}
                    {isChooseAction && (
                      <div css={tw`w-full`}>
                        <FormikDatePicker
                          name="effectiveAt"
                          label="Set effective date"
                          minDate={dayjs().add(1, 'day')}
                          minDateError="Effective date should be at least 1 day in the future"
                          slotProps={{
                            textField: { FormHelperTextProps: { variant: 'standard' } },
                          }}
                          slots={{
                            openPickerIcon: (props) => (
                              <CalendarMinor {...props} width={20} height={20} />
                            ),
                          }}
                        />
                      </div>
                    )}
                    <div css={tw`w-full`}>
                      <FormikDatePicker
                        name="expiresAt"
                        label={
                          isExpirationRequired
                            ? 'Set expiration date'
                            : 'Set expiration date (optional)'
                        }
                        minDate={expirationMinDateOptions.minDate}
                        minDateError={expirationMinDateOptions.minDateError}
                        {...(isExpirationRequired && {
                          maxDate: getExpirationMaxDate(),
                          maxDateError: MAX_DATE_ERROR,
                        })}
                        slotProps={{
                          textField: { FormHelperTextProps: { variant: 'standard' } },
                        }}
                        slots={{
                          openPickerIcon: (props) => (
                            <CalendarMinor {...props} width={20} height={20} />
                          ),
                        }}
                      />
                    </div>
                  </div>
                  <div
                    css={tw`flex justify-between items-center mt-4 pt-3 border-0 border-t border-solid border-[#EDEDED] -mx-4 px-4`}
                  >
                    <Button
                      size="small"
                      color="grey"
                      onClick={onClose}
                      disabled={isLoading}
                      className={[
                        tw`px-8 rounded-lg`,
                        { '& .MuiTypography-root': { fontWeight: 500 } },
                      ]}
                      data-testid="contractor-details-approve-step-cancel-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => formikHandleSubmit()}
                      loading={isLoading}
                      className={tw`px-8 rounded-lg`}
                      data-testid="contractor-details-approve-step-submit-btn"
                    >
                      Approve
                    </Button>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </div>
      </div>
    </Modal>
  )
}

export const ApproveStepModal = memo(ApproveStepModalComponent)
